import { DataImportApiService } from '@admin/core';
import { DataImportCreate } from '@admin/core/models';
import { formControl, Validators } from '@al00x/forms';
import { Component, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { formBuilder, ItemRecord, CacButtonComponent } from '@controladad/ng-base';
import readXlsxFile, { Row } from 'read-excel-file/browser';
import { map, of, switchMap, take, tap } from 'rxjs';

type ImportDateType = 'personnel' | 'property' | 'location';

@Component({
  selector: 'app-import',
  imports: [CacButtonComponent],
  templateUrl: './import.component.html',
})
export class ImportComponent {
  fileInput = viewChild<ElementRef<HTMLInputElement>>('FileInput');

  type = input.required<ImportDateType>();
  internalImport = input<boolean>();

  permission = input<string | undefined>();
  label = input<string>('ایمپورت دیتا');
  disabled = input<boolean>(false);

  refresh = output();
  getData = output<DataImportCreate>();

  private dataImportApi = inject(DataImportApiService);

  triggerFileChange() {
    const input = this.fileInput();
    if (input) {
      input.nativeElement.click();
    }
  }

  onFileChange(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;
    this.clearInput();
    readXlsxFile<string>(file).then((sheets) => {
      const sheet = sheets.at(0);
      if (!sheet) {
        snackbar$.error('صفحه ای برای ایمپورت یافت نشد');
        return;
      }
      const cols = sheet.data.at(0);
      const data = sheet.data.slice(1);
      if (!cols || !data.length) {
        snackbar$.error('دیتایی برای ایمپورت یافت نشد');
        return;
      }
      const items: ItemRecord<string>[] = cols.map((col, i) => ({
        label: col?.toString() ?? `ستون-${i}`,
        value: col?.toString() ?? `ستون-${i}`,
      }));
      const indexMap = new Map(items.map((item, index) => [item.value, index]));
      switch (this.type()) {
        case 'personnel':
          this.importPersonnels(items, indexMap, data);
          break;
        case 'property':
          this.importProperties(items, indexMap, data);
          break;
        case 'location':
          this.importLocations(items, indexMap, data);
          break;
        default:
          break;
      }
    });
  }

  importPersonnels(items: ItemRecord<string>[], indexMap: Map<string, number>, data: Row<string>[]) {
    dialog$
      .input({
        title: 'ایمپورت تحویل گیرنده',
        addButtonText: 'ایمپورت',
        formBuilder: formBuilder({
          cols: 2,
          inputs: {
            externalId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی تحویل گیرنده',
              type: 'select',
              items,
            },
            firstName: {
              control: formControl<string>(undefined, Validators.required),
              label: 'نام',
              type: 'select',
              items,
            },
            lastName: {
              control: formControl<string>(undefined),
              label: 'نام خانوادگی',
              type: 'select',
              items,
            },
            personnelCode: {
              control: formControl<string>(undefined),
              label: 'کد پرسنلی',
              type: 'select',
              items,
            },
          },
        }),
      })
      .afterSubmit()
      .pipe(
        take(1),
        map(result => ({
          personnels: data.map((x) => {
            const item = x as string[];
            return {
              externalId: this.getNumberValue(indexMap, item, result.externalId),
              firstName: this.getValue(indexMap, item, result.firstName),
              lastName: this.getValue(indexMap, item, result.lastName) ?? null,
              personnelCode: this.getValue(indexMap, item, result.personnelCode) ?? null,
            };
          }),
          locations: [],
          properties: [],
        })),
        switchMap(model => this.importData(model))
      )
      .subscribe();
  }

  importProperties(items: ItemRecord<string>[], indexMap: Map<string, number>, data: Row<string>[]) {
    dialog$
      .input({
        title: 'ایمپورت دارایی',
        addButtonText: 'ایمپورت',
        formBuilder: formBuilder({
          cols: 2,
          inputs: {
            externalId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی دارایی',
              type: 'select',
              items,
            },
            name: {
              control: formControl<string>(undefined, Validators.required),
              label: 'اسم',
              type: 'select',
              items,
            },
            code: {
              control: formControl<string>(undefined, Validators.required),
              label: 'کد دارایی',
              type: 'select',
              items,
            },
            tagSerial: {
              control: formControl<string>(undefined),
              label: 'سریال تگ',
              type: 'select',
              items,
            },
            locationId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی مکان',
              type: 'select',
              items,
            },
            mainPersonnelId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی تحویل گیرنده اصلی',
              type: 'select',
              items,
            },
            secondPersonnelId: {
              control: formControl<string>(undefined),
              label: 'شناسه خارجی تحویل گیرنده دوم',
              type: 'select',
              items,
            },
            healthStatus: {
              control: formControl<string>(undefined, Validators.required),
              label: 'وضعیت سلامت',
              type: 'select',
              items,
            },
            tagStatus: {
              control: formControl<string>(undefined, Validators.required),
              label: 'وضعیت تگ',
              type: 'select',
              items,
            },
            tagImagePath: {
              control: formControl<string>(undefined),
              label: 'لینک عکس',
              type: 'select',
              items,
            },
            description: {
              control: formControl<string>(undefined),
              label: 'توضیحات ',
              type: 'select',
              items,
            },
          },
        }),
      })
      .afterSubmit()
      .pipe(
        take(1),
        map((result) => ({
          properties: data.map((x) => {
            const item = x as string[];
            return {
              externalId: this.getNumberValue(indexMap, item, result.externalId),
              name: this.getValue(indexMap, item, result.name),
              code: this.getValue(indexMap, item, result.code),
              tagSerial: this.getValue(indexMap, item, result.tagSerial) || null,
              tagStatus: this.getNumberValue(indexMap, item, result.tagStatus),
              locationExternalId: this.getNumberValue(indexMap, item, result.locationId),
              personnelExternalIds: this.getPersonnelIds(item, indexMap, result.mainPersonnelId, result.secondPersonnelId),
              healthStatus: this.getNumberValue(indexMap, item, result.healthStatus),
              description: this.getValue(indexMap, item, result.description),
              tagImagePaths: this.getPathsArray(this.getValue(indexMap, item, result.tagImagePath)),
              isLost: false,
            };
          }),
          personnels: [],
          locations: [],
        })),
        switchMap(model => this.importData(model))
      )
      .subscribe();
  }

  importLocations(items: ItemRecord<string>[], indexMap: Map<string, number>, data: Row<string>[]) {
    dialog$
      .input({
        title: 'ایمپورت مکان',
        addButtonText: 'ایمپورت',
        formBuilder: formBuilder({
          cols: 2,
          inputs: {
            externalId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی مکان',
              type: 'select',
              items,
            },
            name: {
              control: formControl<string>(undefined, Validators.required),
              label: 'اسم',
              type: 'select',
              items,
            },
            tagSerial: {
              control: formControl<string>(undefined, Validators.required),
              label: 'سریال تگ',
              type: 'select',
              items,
            },
            tagStatus: {
              control: formControl<string>(undefined, Validators.required),
              label: 'وضعیت تگ',
              type: 'select',
              items,
            },
            parentId: {
              control: formControl<string>(undefined, Validators.required),
              label: 'شناسه خارجی مکان والد',
              type: 'select',
              items,
            },
            tagImagePath: {
              control: formControl<string>(undefined),
              label: 'لینک عکس',
              type: 'select',
              items,
            },
            description: {
              control: formControl<string>(undefined),
              label: 'توضیحات',
              type: 'select',
              items,
            },
          },
        }),
      })
      .afterSubmit()
      .pipe(
        take(1),
        map(result => ({
          locations: data.map((x) => {
            const item = x as string[];
            return {
              externalId: this.getNumberValue(indexMap, item, result.externalId),
              name: this.getValue(indexMap, item, result.name),
              tagSerial: this.getValue(indexMap, item, result.tagSerial) || null,
              tagStatus: this.getNumberValue(indexMap, item, result.tagStatus),
              parentExternalId: this.getNumberValue(indexMap, item, result.parentId) || null,
              tagImagePath: this.getValue(indexMap, item, result.tagImagePath) || null,
              description: this.getValue(indexMap, item, result.description),
            };
          }),
          personnels: [],
          properties: [],
        })),
        switchMap(model => this.importData(model))
      )
      .subscribe();
  }

  private getValue(indexMap: Map<string, number>, item: string[], key: string): string {
    const index = indexMap?.get(key);
    return index !== undefined ? (item.at(index) ?? '').toString() : '';
  }

  private getNumberValue(indexMap: Map<string, number>, item: string[], key: string): number {
    return +this.getValue(indexMap, item, key);
  }

  private clearInput() {
    const input = this.fileInput();
    if (input) {
      input.nativeElement.value = '';
    }
  }

  submitted() {
    snackbar$.show('ممکن است دستگاه های جدید بروز نباشند، برای بروزرسانی دستگاه های هندهلد اقدام نمایید', {
      panelClass: 'bg-orange-200 text-orange-700',
      duration: 7000,
    });
    this.refresh.emit();
  }

  private importData(model: DataImportCreate) {
    return this.internalImport() ?
      this.dataImportApi.importData(model).pipe(tap(() => {
        this.submitted();
      })) :
      of(model).pipe(tap(() => {
        this.getData.emit(model);
      }));
  }

  private getPersonnelIds(item: string[], indexMap: Map<string, number>, mainPersonnelId: string, secondPersonnelId: string | undefined) {
    const personnelIds = [this.getNumberValue(indexMap, item, mainPersonnelId)];
    if (secondPersonnelId) {
      const secondId = this.getNumberValue(indexMap, item, secondPersonnelId);
      if (secondId) {
        personnelIds.push(secondId);
      }
    }
    return personnelIds;
  }

  private getPathsArray(paths: string) {
    if (!paths) return [];
    return paths.split(';').map(x => x.trim());
  }
}
